import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

function PageWrapper({ title, subtitle, children }) {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
      <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-400 mb-2 font-body">One Maillot</p>
      <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight uppercase mb-4 text-zinc-900">{title}</h1>
      {subtitle && <p className="text-zinc-500 font-body text-base mb-8">{subtitle}</p>}
      <div className="font-body text-sm text-zinc-600 leading-relaxed space-y-6">{children}</div>
    </div>
  );
}

// ═══════════════════════════════════════════
// À PROPOS
// ═══════════════════════════════════════════
export function AboutPage() {
  return (
    <PageWrapper title="À propos" subtitle="One Maillot — Premium Sportswear">
      <p>One Maillot est une boutique en ligne spécialisée dans les maillots de football premium. Nous proposons une sélection unique de maillots de clubs et de sélections nationales du monde entier.</p>
      <p>Notre mission est simple : offrir aux fans de football un accès facile aux maillots les plus recherchés, avec une qualité irréprochable et un service de livraison rapide.</p>
      <p>Basés aux États-Unis, nous expédions dans le monde entier et nous nous engageons à offrir la meilleure expérience d'achat possible.</p>
    </PageWrapper>
  );
}

// ═══════════════════════════════════════════
// COORDONNÉES / CONTACT
// ═══════════════════════════════════════════
export function ContactPage() {
  return (
    <PageWrapper title="Coordonnées" subtitle="Notre équipe est à votre disposition pour toute question.">
      <div className="space-y-6">
        <div className="flex items-start gap-3">
          <Mail size={18} className="text-zinc-900 mt-0.5 shrink-0" />
          <div><p className="text-zinc-900 font-medium">Email</p><p>onemaillot.app@gmail.com</p></div>
        </div>
        <div className="flex items-start gap-3">
          <MapPin size={18} className="text-zinc-900 mt-0.5 shrink-0" />
          <div><p className="text-zinc-900 font-medium">Adresse</p><p>One Maillot<br />États-Unis</p></div>
        </div>
        <div className="flex items-start gap-3">
          <Phone size={18} className="text-zinc-900 mt-0.5 shrink-0" />
          <div><p className="text-zinc-900 font-medium">Support</p><p>Du lundi au vendredi, 9h-18h (EST)<br />Réponse sous 24h par email</p></div>
        </div>
      </div>
      <div className="border-t border-zinc-200 pt-6 mt-8">
        <p className="text-xs text-zinc-400">Pour toute question relative à une commande, merci d'inclure votre numéro de commande dans votre message.</p>
      </div>
    </PageWrapper>
  );
}

// ═══════════════════════════════════════════
// FAQ
// ═══════════════════════════════════════════
export function FAQPage() {
  const faqs = [
    { q: "Quels sont les délais de livraison ?", a: "L'expédition standard est estimée entre 10 et 15 jours ouvrés. L'expédition express (si disponible) entre 5 et 8 jours ouvrés. Un délai de traitement de 2 à 3 jours s'ajoute à chaque commande." },
    { q: "La livraison est-elle vraiment gratuite ?", a: "Oui, la livraison est offerte sur toutes nos commandes, sans minimum d'achat, partout dans le monde." },
    { q: "Comment suivre ma commande ?", a: "Dès que votre commande est expédiée, vous recevrez un email avec un numéro de suivi vous permettant de suivre votre colis en temps réel." },
    { q: "Puis-je retourner un produit ?", a: "Nous n'acceptons pas les retours de produits. Cependant, un remboursement peut être envisagé si le produit est défectueux, endommagé ou non conforme. Contactez-nous dans les 7 jours suivant la réception." },
    { q: "Comment obtenir un remboursement ?", a: "Contactez-nous à onemaillot.app@gmail.com avec votre numéro de commande et des photos du problème. Nous traitons les demandes sous 5 jours ouvrés." },
    { q: "Les maillots sont-ils officiels ?", a: "One Maillot propose des maillots de qualité premium. Nous ne sommes ni affiliés, ni autorisés, ni sponsorisés par les clubs, fédérations ou marques sportives. Les noms et logos sont utilisés à titre descriptif." },
    { q: "Quels moyens de paiement acceptez-vous ?", a: "Nous acceptons les cartes bancaires (Visa, Mastercard, American Express) via notre plateforme de paiement sécurisée." },
  ];
  return (
    <PageWrapper title="FAQ" subtitle="Questions fréquemment posées">
      <Accordion type="single" collapsible className="w-full">
        {faqs.map((f, i) => (
          <AccordionItem key={i} value={`faq-${i}`} className="border-zinc-200">
            <AccordionTrigger className="text-sm text-zinc-900 font-medium text-left">{f.q}</AccordionTrigger>
            <AccordionContent className="text-sm text-zinc-500">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </PageWrapper>
  );
}

// ═══════════════════════════════════════════
// POLITIQUE D'EXPÉDITION
// ═══════════════════════════════════════════
export function LivraisonPage() {
  return (
    <PageWrapper title="Politique d'expédition" subtitle="Nous faisons de notre mieux pour expédier vos commandes dans les meilleurs délais.">
      <div>
        <h3 className="text-zinc-900 font-bold text-sm mb-2">1. Zones de livraison</h3>
        <p>Nous expédions nos produits dans le monde entier. Si votre zone géographique n'est pas couverte, n'hésitez pas à nous contacter pour vérifier la possibilité d'une expédition spéciale.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">2. Délais de livraison</h3>
        <p>Les délais varient selon la destination et le mode d'expédition choisi :</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li><strong>Expédition standard</strong> : estimée entre 10 et 15 jours ouvrés + 2-3 jours de traitement</li>
          <li><strong>Expédition express</strong> (si disponible) : estimée entre 5 et 8 jours ouvrés + 2-3 jours de traitement</li>
        </ul>
        <p className="mt-2">Les délais indiqués sont des estimations et peuvent varier en fonction de facteurs indépendants de notre volonté (retards de transport, conditions climatiques, douanes).</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">3. Frais de livraison</h3>
        <p>La livraison est offerte sur toutes nos commandes, sans minimum d'achat.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">4. Suivi de la commande</h3>
        <p>Une fois votre commande expédiée, vous recevrez un email de confirmation avec un numéro de suivi pour suivre votre colis en temps réel jusqu'à sa livraison.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">5. Problèmes de livraison</h3>
        <p>Si vous ne recevez pas votre commande dans le délai estimé, contactez notre service client à <strong>onemaillot.app@gmail.com</strong>. Nous ne pouvons être tenus responsables de la perte ou du retard de livraison dû à une adresse incorrecte fournie par le client.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">6. Taxes et droits de douane</h3>
        <p>Pour les commandes internationales, des frais de douane ou des taxes peuvent être appliqués à l'arrivée du colis dans le pays de destination. Ces frais sont à la charge du client. Nous vous recommandons de vérifier auprès des autorités locales.</p>

        <p className="mt-8 text-xs text-zinc-400">Contact : onemaillot.app@gmail.com</p>
      </div>
    </PageWrapper>
  );
}

// ═══════════════════════════════════════════
// POLITIQUE DE REMBOURSEMENT
// ═══════════════════════════════════════════
export function RetoursPage() {
  return (
    <PageWrapper title="Politique de remboursement" subtitle="Nous mettons tout en oeuvre pour que vous soyez satisfait de votre commande.">
      <div>
        <h3 className="text-zinc-900 font-bold text-sm mb-2">1. Notre engagement</h3>
        <p>Chez One Maillot, votre satisfaction est notre priorité. En cas de problème avec votre commande, <strong>nous vous renvoyons le produit correct gratuitement</strong>. Nous ne procédons pas à des remboursements mais nous nous engageons à résoudre chaque situation en vous réexpédiant un article conforme.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">2. Cas pris en charge</h3>
        <p>Un renvoi gratuit du produit sera effectué dans les situations suivantes :</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li><strong>Produit défectueux ou endommagé</strong> : si le produit présente un défaut de fabrication ou a été endommagé lors de la livraison, nous vous renvoyons un produit neuf.</li>
          <li><strong>Erreur de commande de notre part</strong> : si vous recevez une mauvaise taille ou un mauvais modèle, nous vous réexpédions le bon article.</li>
          <li><strong>Produit non conforme</strong> : si le produit reçu ne correspond pas à la description affichée sur notre site, nous vous renvoyons le produit correct.</li>
        </ul>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">3. Procédure</h3>
        <p>Contactez-nous à <strong>onemaillot.app@gmail.com</strong> dans un délai de <strong>7 jours</strong> suivant la réception de votre commande, en indiquant :</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Votre numéro de commande</li>
          <li>Des photos du produit reçu montrant le problème</li>
          <li>Une description du problème rencontré</li>
        </ul>
        <p className="mt-2">Nous étudierons votre demande sous <strong>5 jours ouvrés</strong> et procéderons à la réexpédition du bon produit dès validation.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">4. Délai de réexpédition</h3>
        <p>Une fois votre demande validée, le nouveau produit sera expédié sous <strong>3 à 5 jours ouvrés</strong>. Les délais de livraison habituels s'appliquent ensuite (10-15 jours standard, 5-8 jours express). La réexpédition est entièrement gratuite.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">5. Cas non pris en charge</h3>
        <p>Les réexpéditions ne seront pas accordées dans les cas suivants :</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Usure normale du produit après utilisation</li>
          <li>Dommages causés par une utilisation inappropriée</li>
          <li>Modification ou altération du produit par le client</li>
          <li>Demande effectuée au-delà du délai de 7 jours</li>
        </ul>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">6. Retour du produit défectueux</h3>
        <p>Vous n'avez <strong>pas besoin de nous retourner</strong> le produit défectueux. Les photos fournies lors de votre demande suffisent à valider le renvoi.</p>

        <p className="mt-8 text-xs text-zinc-400">Contact : onemaillot.app@gmail.com — One Maillot</p>
      </div>
    </PageWrapper>
  );
}

// ═══════════════════════════════════════════
// CONDITIONS GÉNÉRALES DE VENTE
// ═══════════════════════════════════════════
export function CGVPage() {
  return (
    <PageWrapper title="Conditions générales de vente" subtitle="Dernière mise à jour : avril 2026">
      <div>
        <h3 className="text-zinc-900 font-bold text-sm mb-2">1. Généralités</h3>
        <p>Les présentes Conditions Générales de Vente (CGV) régissent l'ensemble des ventes effectuées sur le site www.onemaillot.com, exploité par One Maillot.</p>
        <p className="mt-2">En passant commande sur notre site, vous acceptez sans réserve les présentes CGV.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">2. Produits et prix</h3>
        <p>Tous les produits proposés sont décrits et représentés le plus fidèlement possible. Cependant, nous ne garantissons pas que les descriptions ou photos soient exemptes d'erreurs. Les prix affichés sont en euros et nous nous réservons le droit de les modifier sans préavis. Les promotions peuvent être soumises à des conditions spécifiques.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">3. Commandes et paiements</h3>
        <p>En passant commande, vous vous engagez à fournir des informations complètes et exactes. Toute commande est soumise à disponibilité et à notre confirmation. Les paiements doivent être effectués par les méthodes acceptées sur le site. Nous nous réservons le droit de refuser ou d'annuler une commande en cas de suspicion de fraude ou de violation des présentes conditions.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">4. Livraison</h3>
        <p>Les délais de livraison varient en fonction des produits et de votre localisation. Bien que nous nous efforcions de respecter les délais indiqués, nous ne pouvons garantir l'absence de retards. La livraison est offerte sur toutes les commandes. Nous ne pouvons être tenus responsables des retards ou problèmes causés par des tiers.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">5. Politique de renvoi</h3>
        <p>Nous ne procédons pas à des remboursements. En cas de problème avec votre commande (produit défectueux, erreur, non-conformité), nous vous réexpédions gratuitement le bon produit. Pour plus de détails, consultez notre <Link to="/retours" className="text-zinc-900 underline">Politique de remboursement</Link>.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">6. Propriété intellectuelle</h3>
        <p>Tous les éléments présents sur notre site (textes, images, logos, graphiques) sont la propriété de One Maillot ou de ses partenaires et sont protégés par les lois applicables en matière de propriété intellectuelle. Toute utilisation non autorisée est strictement interdite.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">7. Limitation de responsabilité</h3>
        <p>Nous déclinons toute responsabilité en cas de dommages indirects, accessoires, spéciaux ou consécutifs liés à l'utilisation ou à l'incapacité d'utiliser nos produits ou services, dans la mesure permise par la loi.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">8. Droit applicable</h3>
        <p>Les présentes CGV sont régies par les lois applicables. Tout litige sera soumis aux tribunaux compétents.</p>

        <p className="mt-8 text-xs text-zinc-400">Contact : onemaillot.app@gmail.com</p>
      </div>
    </PageWrapper>
  );
}

// ═══════════════════════════════════════════
// POLITIQUE DE CONFIDENTIALITÉ
// ═══════════════════════════════════════════
export function ConfidentialitePage() {
  return (
    <PageWrapper title="Politique de confidentialité" subtitle="Dernière mise à jour : avril 2026">
      <div>
        <p>One Maillot exploite cette boutique et ce site web afin de vous fournir une expérience d'achat personnalisée (ci-après les "Services"). La présente Politique de confidentialité décrit la façon dont nous collectons, utilisons et divulguons vos informations personnelles.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">Les informations personnelles que nous collectons</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Coordonnées</strong> : nom, adresse, adresse de facturation et livraison, numéro de téléphone, email.</li>
          <li><strong>Informations financières</strong> : numéros de carte, détails de paiement et de transaction.</li>
          <li><strong>Informations du compte</strong> : nom d'utilisateur, mot de passe, préférences.</li>
          <li><strong>Informations de transaction</strong> : articles consultés, ajoutés au panier, achetés, retournés.</li>
          <li><strong>Communications</strong> : informations incluses dans vos messages au service client.</li>
          <li><strong>Informations sur l'appareil</strong> : navigateur, adresse IP, identifiants uniques.</li>
          <li><strong>Informations d'utilisation</strong> : comment et quand vous accédez aux Services.</li>
        </ul>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">Comment nous utilisons vos informations</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Fournir et améliorer les Services</strong> : traiter vos paiements, commandes, expéditions, retours et vous recommander des produits.</li>
          <li><strong>Marketing</strong> : vous envoyer des communications promotionnelles par email (avec possibilité de désabonnement).</li>
          <li><strong>Sécurité</strong> : détecter et prévenir les activités frauduleuses.</li>
          <li><strong>Communication</strong> : répondre à vos demandes et assurer le service client.</li>
          <li><strong>Obligations légales</strong> : nous conformer à la législation applicable.</li>
        </ul>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">Divulgation des informations</h3>
        <p>Nous pouvons divulguer vos informations à des prestataires de services (paiement, expédition, hébergement), à des partenaires marketing, dans le cadre d'une transaction commerciale, ou pour respecter nos obligations légales.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">Données des enfants</h3>
        <p>Les Services ne sont pas destinés aux enfants. Nous ne collectons sciemment aucune information personnelle concernant des mineurs.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">Sécurité et conservation</h3>
        <p>Nous mettons en oeuvre des mesures de sécurité appropriées pour protéger vos informations. La durée de conservation dépend de la nécessité de gérer votre compte, fournir les Services et respecter nos obligations légales.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">Vos droits</h3>
        <p>En fonction de votre lieu de résidence, vous pouvez disposer de droits d'accès, de rectification, de suppression et de portabilité de vos données. Pour exercer ces droits, contactez-nous à onemaillot.app@gmail.com.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">Cookies</h3>
        <p>Notre site utilise des cookies nécessaires au fonctionnement du panier et de l'authentification. Vous pouvez désactiver les cookies via les paramètres de votre navigateur.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">Transferts internationaux</h3>
        <p>Vos informations peuvent être transférées et traitées aux États-Unis, où se situe notre siège social. Nous nous appuyons sur des mécanismes de transfert reconnus pour assurer un niveau de protection adéquat.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">Modifications</h3>
        <p>Nous pouvons mettre à jour cette Politique à tout moment. La version révisée sera publiée sur cette page avec la date de mise à jour.</p>

        <p className="mt-8 text-xs text-zinc-400">Contact : onemaillot.app@gmail.com — One Maillot</p>
      </div>
    </PageWrapper>
  );
}

// ═══════════════════════════════════════════
// MENTIONS LÉGALES
// ═══════════════════════════════════════════
export function MentionsLegalesPage() {
  return (
    <PageWrapper title="Mentions légales" subtitle="Informations légales relatives au site One Maillot">
      <div>
        <h3 className="text-zinc-900 font-bold text-sm mb-2">1. Informations sur l'entreprise</h3>
        <p><strong>Email de contact</strong> : onemaillot.app@gmail.com</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">2. Hébergement</h3>
        <p><strong>Hébergeur du site</strong> : Vercel Inc.<br />340 S Lemon Ave #4133, Walnut, CA 91789, États-Unis<br />Site web : <a href="https://vercel.com" className="underline" target="_blank" rel="noopener noreferrer">www.vercel.com</a></p>
        <p className="mt-2"><strong>Base de données</strong> : Supabase Inc.<br />970 Toa Payoh North #07-04, Singapore 318992<br />Site web : <a href="https://supabase.com" className="underline" target="_blank" rel="noopener noreferrer">www.supabase.com</a></p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">3. Propriété intellectuelle et nature des produits</h3>
        <p>L'ensemble du contenu du site www.onemaillot.com, y compris les textes, images, logos, graphiques et autres contenus, est protégé par les lois américaines et internationales en matière de propriété intellectuelle.</p>
        <p className="mt-2"><strong>Nature des produits</strong> : One Maillot commercialise des articles de sport et équipements inspirés du football, fournis par des partenaires indépendants. Nous ne fabriquons pas ces produits et ne revendiquons aucun droit de propriété sur les marques, logos et éléments distinctifs visibles sur certains articles.</p>
        <p className="mt-2"><strong>Absence d'affiliation</strong> : One Maillot n'est ni affilié, ni autorisé, ni sponsorisé par Nike, Adidas, Puma, les clubs de football, les fédérations sportives ou tout autre détenteur de droits. Les noms d'équipes, joueurs, logos ou designs sont utilisés uniquement à des fins descriptives. Tous les droits et marques déposées appartiennent à leurs propriétaires respectifs.</p>
        <p className="mt-2">Si un détenteur de droits considère qu'un article porte atteinte à sa marque, il peut nous contacter à onemaillot.app@gmail.com. Nous nous engageons à analyser la demande et à prendre les mesures appropriées.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">4. Protection des données personnelles</h3>
        <p>One Maillot collecte et utilise certaines données personnelles uniquement pour le bon fonctionnement du site et la gestion des commandes. Pour plus de détails, consultez notre <Link to="/confidentialite" className="text-zinc-900 underline">Politique de confidentialité</Link>.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">5. Responsabilité</h3>
        <p>One Maillot met tout en oeuvre pour garantir l'exactitude des informations sur son site. Cependant, nous ne pouvons être tenus responsables des éventuelles erreurs ou omissions, des interruptions du site, ou du contenu de sites tiers accessibles via des liens.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">6. Droit applicable</h3>
        <p>Les présentes mentions légales sont régies par les lois applicables. En cas de litige, nous encourageons une résolution amiable. Avant toute action en justice, nous encourageons nos clients à nous contacter pour tenter une résolution amiable.</p>

        <p className="mt-8 text-xs text-zinc-400">Dernière mise à jour : avril 2026 — Contact : onemaillot.app@gmail.com</p>
      </div>
    </PageWrapper>
  );
}

// ═══════════════════════════════════════════
// CONDITIONS D'UTILISATION
// ═══════════════════════════════════════════
export function ConditionsUtilisationPage() {
  return (
    <PageWrapper title="Conditions d'utilisation" subtitle="En accédant à ce site, vous acceptez les conditions suivantes.">
      <div>
        <h3 className="text-zinc-900 font-bold text-sm mb-2">1. Généralités</h3>
        <p>Ces Conditions d'utilisation s'appliquent à tous les utilisateurs du site www.onemaillot.com, exploité par One Maillot. En utilisant notre site, vous confirmez avoir pris connaissance de ces Conditions et les accepter sans réserve.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">2. Accès au site</h3>
        <p>L'accès au site One Maillot est gratuit. Les frais d'accès à internet restent à votre charge. One Maillot se réserve le droit de modifier, suspendre ou interrompre l'accès au site à tout moment.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">3. Compte utilisateur</h3>
        <p>La création d'un compte est nécessaire pour passer commande. Vous êtes responsable de la confidentialité de vos identifiants. Tout usage non autorisé de votre compte doit nous être signalé immédiatement à onemaillot.app@gmail.com.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">4. Utilisation acceptable</h3>
        <p>En utilisant ce site, vous acceptez de ne pas :</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Utiliser le site pour des activités frauduleuses ou illégales</li>
          <li>Télécharger ou transmettre des virus ou tout code nuisible</li>
          <li>Interférer ou compromettre la sécurité ou le fonctionnement du site</li>
          <li>Reproduire, dupliquer ou exploiter le contenu du site sans autorisation</li>
        </ul>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">5. Propriété intellectuelle</h3>
        <p>Tous les éléments présents sur notre site (textes, images, logos, graphiques) sont la propriété de One Maillot ou de ses partenaires et sont protégés par les lois sur la propriété intellectuelle. Toute utilisation non autorisée est strictement interdite.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">6. Limitation de responsabilité</h3>
        <p>Nous déclinons toute responsabilité en cas de dommages indirects liés à l'utilisation du site. Nous ne garantissons pas que le site sera ininterrompu, sécurisé ou exempt d'erreurs. Votre utilisation se fait à vos propres risques.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">7. Modifications</h3>
        <p>Nous nous réservons le droit de modifier ces Conditions à tout moment. Les modifications prennent effet immédiatement après leur publication.</p>

        <h3 className="text-zinc-900 font-bold text-sm mt-6 mb-2">8. Droit applicable</h3>
        <p>Ces Conditions sont régies par les lois applicables. Tout litige sera soumis aux tribunaux compétents.</p>

        <p className="mt-8 text-xs text-zinc-400">Contact : onemaillot.app@gmail.com — One Maillot</p>
      </div>
    </PageWrapper>
  );
}
